import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDate, todayISO } from "@/lib/utils2";

const empty = { party_id: "", po_number: "", po_date: todayISO(), notes: "" };

export default function POs() {
  const [items, setItems] = useState([]);
  const [parties, setParties] = useState([]);
  const [filterParty, setFilterParty] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const params = filterParty !== "all" ? { party_id: filterParty } : {};
    const [pos, pp] = await Promise.all([api.get("/pos", { params }), api.get("/parties")]);
    setItems(pos.data); setParties(pp.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterParty]);

  const partyName = (id) => parties.find((p) => p.id === id)?.name || "—";

  const save = async () => {
    if (!form.party_id || !form.po_number.trim()) return toast.error("Party & PO number required");
    try {
      if (editId) await api.put(`/pos/${editId}`, form);
      else await api.post("/pos", form);
      toast.success("Saved");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this PO?")) return;
    await api.delete(`/pos/${id}`); load();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-5xl mx-auto" data-testid="pos-page">
      <PageHeader
        title="Purchase Orders (POs)"
        subtitle="Manage all incoming POs from parties. Pick from this list when creating a truck entry."
        action={
          <Button data-testid="add-po-btn" onClick={() => { setEditId(null); setForm({ ...empty, date: todayISO() }); setOpen(true); }}
            className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add PO
          </Button>
        }
      />

      <Card className="rounded-sm border-brand-line shadow-none p-4 mb-5">
        <div className="flex items-end gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">Filter by party</Label>
            <Select value={filterParty} onValueChange={setFilterParty}>
              <SelectTrigger className="mt-1.5 w-64 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Parties</SelectItem>
                {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="text-left px-5 py-3">PO Number</th>
              <th className="text-left px-3 py-3">PO Date</th>
              <th className="text-left px-3 py-3">Party</th>
              <th className="text-left px-3 py-3">Notes</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-brand-muted text-sm">No POs yet.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-brand-line hover:bg-secondary/40" data-testid={`po-row-${p.id}`}>
                <td className="px-5 py-3 font-mono">{p.po_number}</td>
                <td className="px-3 py-3">{formatDate(p.po_date)}</td>
                <td className="px-3 py-3 font-medium">{partyName(p.party_id)}</td>
                <td className="px-3 py-3 text-brand-muted">{p.notes || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditId(p.id); setForm({ ...empty, ...p }); setOpen(true); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-brand-terracotta" onClick={() => remove(p.id)} data-admin-only="true">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} Purchase Order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Party *</Label>
              <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                <SelectTrigger data-testid="po-party-select" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue placeholder="Select party" /></SelectTrigger>
                <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>PO Number *</Label><Input data-testid="po-number-input" className="mt-1.5 h-10 rounded-sm bg-white font-mono" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} /></div>
            <div><Label>PO Date</Label><Input type="date" className="mt-1.5 h-10 rounded-sm bg-white" value={form.po_date} onChange={(e) => setForm({ ...form, po_date: e.target.value })} /></div>
            <div><Label>Notes</Label><Input className="mt-1.5 h-10 rounded-sm bg-white" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-po-btn" className="rounded-sm bg-brand-forest text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
